// HotTubQuiz_Landing_v01.tsx
// -----------------------------------------------------------------------------
// HotTub Quiz • Lead-Gen Landing Page (React + TypeScript + React-Bootstrap)
// Version: v01
// Date: 2025-09-01 (America/Denver)
// Notes:
// - Single-page layout with hero Carousel + right-aligned quiz card.
// - Strong field validation; displays a clean confirmation summary on success.
// - Modular payload builder -> wire strings -> postLead() stub (swap with API).
// - No external images required; replace placeholder image paths as desired.
// -----------------------------------------------------------------------------

import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Carousel,
} from "react-bootstrap";

type InstallLocation = "Patio" | "Deck" | "Backyard" | "Indoor";
type Seating = "2-3" | "4-5" | "6-7" | "8+";
type Power = "110V (Plug & Play)" | "220V (Dedicated)" | "Not sure";
type Timeline = "ASAP" | "1-3 months" | "3-6 months" | "6+ months";
type Budget =
  | "Under $5,000"
  | "$5,000–$8,000"
  | "$8,000–$12,000"
  | "$12,000–$18,000"
  | "$18,000+";

type FeatureKey =
  | "hydrotherapyJets"
  | "loungerSeat"
  | "ledLighting"
  | "waterfall"
  | "saltOrOzone"
  | "wifiApp"
  | "energyEfficient"
  | "coverLifter";

interface HotTubQuizForm {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  zip: string;

  timeline: Timeline | "";
  budget: Budget | "";
  seating: Seating | "";
  installLocation: InstallLocation | "";
  power: Power | "";

  features: Record<FeatureKey, boolean>;

  hasExistingSlab: "" | "Yes" | "No" | "Not sure";
  hasElectrical: "" | "Yes" | "No" | "Not sure";
  accessWidthInches: string; // string so it's easy to serialize/validate
  notes: string;

  contactPreference: "" | "Call" | "Text" | "Email";
  bestTimeToReach: "" | "Morning" | "Afternoon" | "Evening";
  consent: boolean;
}

const defaultForm: HotTubQuizForm = {
  fullName: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  zip: "",

  timeline: "",
  budget: "",
  seating: "",
  installLocation: "",
  power: "",

  features: {
    hydrotherapyJets: false,
    loungerSeat: false,
    ledLighting: false,
    waterfall: false,
    saltOrOzone: false,
    wifiApp: false,
    energyEfficient: false,
    coverLifter: false,
  },

  hasExistingSlab: "",
  hasElectrical: "",
  accessWidthInches: "",
  notes: "",

  contactPreference: "",
  bestTimeToReach: "",
  consent: false,
};

// ----------------------------- Utilities -------------------------------------

const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const phoneOk = (v: string) => /^\+?[\d\s().-]{7,}$/.test(v.trim());
const zipOk = (v: string) => /^\d{5}(-\d{4})?$/.test(v.trim());

const required = (v: string | boolean) =>
  typeof v === "string" ? v.trim().length > 0 : Boolean(v);

// Convert typed form to flat string payload (wire format friendly)
function toWireStrings(form: HotTubQuizForm) {
  const featureList = Object.entries(form.features)
    .filter(([, on]) => on)
    .map(([k]) => k)
    .join(", ");

  return {
    fullName: String(form.fullName || ""),
    email: String(form.email || ""),
    phone: String(form.phone || ""),
    city: String(form.city || ""),
    state: String(form.state || ""),
    zip: String(form.zip || ""),

    timeline: String(form.timeline || ""),
    budget: String(form.budget || ""),
    seating: String(form.seating || ""),
    installLocation: String(form.installLocation || ""),
    power: String(form.power || ""),

    features: featureList,
    hasExistingSlab: String(form.hasExistingSlab || ""),
    hasElectrical: String(form.hasElectrical || ""),
    accessWidthInches: String(form.accessWidthInches || ""),
    notes: String(form.notes || ""),

    contactPreference: String(form.contactPreference || ""),
    bestTimeToReach: String(form.bestTimeToReach || ""),

    consent: String(form.consent),
    // Optional marketing meta
    itemCategory: "lead.hottub.quiz",
    itemClass: "quote.request",
    itemType: "HotTubQuizLanding.v01",
    dateTimeStamp: new Date().toISOString(),
  };
}

// Simulated async submit; replace with your API call
async function postLead(payload: Record<string, string>) {
  // Example: replace with fetch('/api/lead', { method: 'POST', body: JSON.stringify(payload) })
  // Simulate latency
  await new Promise((r) => setTimeout(r, 900));
  // Simulate success
  return { ok: true, id: cryptoRandomId() };
}

function cryptoRandomId() {
  // Lightweight ID for demo (not cryptographically secure)
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ----------------------------- Component -------------------------------------

export const HotTubQuiz_Landing_v01: React.FC = () => {
  const [form, setForm] = useState<HotTubQuizForm>(defaultForm);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmation, setConfirmation] =
    useState<null | { id: string; payload: Record<string, string> }>(null);

  const featuresArray = useMemo(
    () =>
      [
        { key: "hydrotherapyJets", label: "Hydrotherapy jets" },
        { key: "loungerSeat", label: "Lounger seat" },
        { key: "ledLighting", label: "LED lighting" },
        { key: "waterfall", label: "Waterfall" },
        { key: "saltOrOzone", label: "Salt/Ozone system" },
        { key: "wifiApp", label: "Wi-Fi / App control" },
        { key: "energyEfficient", label: "Energy efficient" },
        { key: "coverLifter", label: "Cover lifter" },
      ] as Array<{ key: FeatureKey; label: string }>,
    []
  );

  const canSubmit = useMemo(() => {
    return (
      required(form.fullName) &&
      emailOk(form.email) &&
      phoneOk(form.phone) &&
      required(form.city) &&
      required(form.state) &&
      zipOk(form.zip) &&
      required(form.timeline) &&
      required(form.budget) &&
      required(form.seating) &&
      required(form.installLocation) &&
      required(form.power) &&
      form.consent
    );
  }, [form]);

  function update<K extends keyof HotTubQuizForm>(key: K, value: HotTubQuizForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    setValidated(true);
    setServerError(null);

    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const wire = toWireStrings(form);
      const res = await postLead(wire);
      if (res?.ok && res?.id) {
        setConfirmation({ id: res.id, payload: wire });
        // Optional: reset form
        setForm(defaultForm);
        setValidated(false);
      } else {
        throw new Error("Unexpected server response.");
      }
    } catch (err: any) {
      setServerError(err?.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // -------------------------- Render ----------------------------------------

  return (
    <div className="bg-body-tertiary">
      {/* HERO with Carousel */}
      <section className="position-relative">
        <Carousel indicators={true} controls={true} fade interval={6000}>
          {[
            {
              src: "/images/hottub/hero1.jpg",
              alt: "Relaxing hot tub under string lights on a patio",
              title: "Find Your Perfect Hot Tub",
              sub: "Answer a few quick questions. We’ll match models & pricing to your space and budget.",
              cap: "Personalized recommendations in minutes.",
            },
            {
              src: "/images/hottub/hero2.jpg",
              alt: "Family enjoying a spa in the backyard",
              title: "Hydrotherapy at Home",
              sub: "Options from plug-and-play to premium therapy jets.",
              cap: "Wellness, comfort, and energy efficiency.",
            },
            {
              src: "/images/hottub/hero3.jpg",
              alt: "Modern deck with a recessed spa",
              title: "Install Made Simple",
              sub: "We’ll guide you on site prep, electrical, and access.",
              cap: "From quote to soak—stress free.",
            },
          ].map((s, i) => (
            <Carousel.Item key={i}>
              {/* Use a tall image area; adjust height as needed */}
              <div
                style={{
                  height: "58vh",
                  backgroundImage: `url('${s.src}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "brightness(0.75)",
                }}
              />
              <Carousel.Caption className="text-start">
                <h2 className="fw-bold">{s.title}</h2>
                <p className="lead">{s.sub}</p>
                <small className="d-block mt-2">
                  <Badge bg="light" text="dark">
                    {s.cap}
                  </Badge>
                </small>
              </Carousel.Caption>
            </Carousel.Item>
          ))}
        </Carousel>
      </section>

      {/* CONTENT: Form Card */}
      <Container className="py-5">
        {!confirmation ? (
          <Row className="g-4 align-items-start">
            <Col lg={7}>
              <h1 className="display-6 mb-2">HotTub Quiz</h1>
              <p className="text-muted fs-5">
                Tell us about your space and preferences. We’ll suggest models, provide estimated
                pricing, and connect you with a local specialist.
              </p>
              <Row className="g-3 mt-1">
                <Col sm={6}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <Card.Title className="h6">Why this quiz?</Card.Title>
                      <ul className="mb-0 ps-3 small">
                        <li>Match features to your wellness goals</li>
                        <li>Understand site & power needs</li>
                        <li>Get accurate, personalized pricing</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <Card.Title className="h6">What you’ll get</Card.Title>
                      <ul className="mb-0 ps-3 small">
                        <li>Top 2–3 model recommendations</li>
                        <li>Install guidance checklist</li>
                        <li>Exclusive promotions (if available)</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>

            <Col lg={5}>
              <Card className="shadow-lg">
                <Card.Header className="bg-primary text-white fw-semibold">
                  Quick Quiz & Quote
                </Card.Header>
                <Card.Body>
                  <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    {/* Contact */}
                    <h6 className="mb-2">Contact Info</h6>
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Group controlId="fullName">
                          <Form.Label>Full Name *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            placeholder="Jane Doe"
                            value={form.fullName}
                            onChange={(e) => update("fullName", e.target.value)}
                            isInvalid={validated && !required(form.fullName)}
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter your name.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email *</Form.Label>
                          <Form.Control
                            required
                            type="email"
                            placeholder="you@example.com"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            isInvalid={validated && !emailOk(form.email)}
                          />
                          <Form.Control.Feedback type="invalid">
                            Enter a valid email.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="phone">
                          <Form.Label>Phone *</Form.Label>
                          <Form.Control
                            required
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            isInvalid={validated && !phoneOk(form.phone)}
                          />
                          <Form.Control.Feedback type="invalid">
                            Enter a valid phone number.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group controlId="city">
                          <Form.Label>City *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            value={form.city}
                            onChange={(e) => update("city", e.target.value)}
                            isInvalid={validated && !required(form.city)}
                          />
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="state">
                          <Form.Label>State *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            placeholder="CO"
                            value={form.state}
                            onChange={(e) => update("state", e.target.value.toUpperCase())}
                            maxLength={2}
                            isInvalid={validated && !(form.state.trim().length === 2)}
                          />
                          <Form.Control.Feedback type="invalid">
                            Use 2-letter code.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="zip">
                          <Form.Label>ZIP *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            placeholder="80202"
                            value={form.zip}
                            onChange={(e) => update("zip", e.target.value)}
                            isInvalid={validated && !zipOk(form.zip)}
                          />
                          <Form.Control.Feedback type="invalid">
                            Enter a valid ZIP.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr className="my-3" />

                    {/* Preferences */}
                    <h6 className="mb-2">Your Preferences</h6>
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group controlId="timeline">
                          <Form.Label>Timeline *</Form.Label>
                          <Form.Select
                            required
                            value={form.timeline}
                            onChange={(e) => update("timeline", e.target.value as Timeline | "")}
                            isInvalid={validated && !required(form.timeline)}
                          >
                            <option value="">Select timeline</option>
                            <option>ASAP</option>
                            <option>1-3 months</option>
                            <option>3-6 months</option>
                            <option>6+ months</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Select a timeline.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="budget">
                          <Form.Label>Budget *</Form.Label>
                          <Form.Select
                            required
                            value={form.budget}
                            onChange={(e) => update("budget", e.target.value as Budget | "")}
                            isInvalid={validated && !required(form.budget)}
                          >
                            <option value="">Select budget</option>
                            <option>Under $5,000</option>
                            <option>$5,000–$8,000</option>
                            <option>$8,000–$12,000</option>
                            <option>$12,000–$18,000</option>
                            <option>$18,000+</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Select a budget.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="seating">
                          <Form.Label>Seating Capacity *</Form.Label>
                          <Form.Select
                            required
                            value={form.seating}
                            onChange={(e) => update("seating", e.target.value as Seating | "")}
                            isInvalid={validated && !required(form.seating)}
                          >
                            <option value="">Select capacity</option>
                            <option>2-3</option>
                            <option>4-5</option>
                            <option>6-7</option>
                            <option>8+</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Select seating capacity.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="installLocation">
                          <Form.Label>Install Location *</Form.Label>
                          <Form.Select
                            required
                            value={form.installLocation}
                            onChange={(e) =>
                              update("installLocation", e.target.value as InstallLocation | "")
                            }
                            isInvalid={validated && !required(form.installLocation)}
                          >
                            <option value="">Select location</option>
                            <option>Patio</option>
                            <option>Deck</option>
                            <option>Backyard</option>
                            <option>Indoor</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Select an install location.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="power">
                          <Form.Label>Power *</Form.Label>
                          <Form.Select
                            required
                            value={form.power}
                            onChange={(e) => update("power", e.target.value as Power | "")}
                            isInvalid={validated && !required(form.power)}
                          >
                            <option value="">Select power</option>
                            <option>110V (Plug & Play)</option>
                            <option>220V (Dedicated)</option>
                            <option>Not sure</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Select a power option.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="accessWidthInches">
                          <Form.Label>Min. Access Width (inches)</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              min={0}
                              placeholder="e.g., 40"
                              value={form.accessWidthInches}
                              onChange={(e) => update("accessWidthInches", e.target.value)}
                            />
                            <InputGroup.Text>in</InputGroup.Text>
                          </InputGroup>
                          <Form.Text className="text-muted">
                            Gate/path clearance for delivery (optional).
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Features */}
                    <div className="mt-3">
                      <Form.Label className="fw-semibold">Desired Features</Form.Label>
                      <Row xs={1} sm={2}>
                        {featuresArray.map((f) => (
                          <Col key={f.key} className="mb-1">
                            <Form.Check
                              type="checkbox"
                              id={`feat-${f.key}`}
                              label={f.label}
                              checked={form.features[f.key]}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  features: { ...prev.features, [f.key]: e.target.checked },
                                }))
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {/* Site readiness */}
                    <Row className="g-2 mt-2">
                      <Col md={6}>
                        <Form.Group controlId="hasExistingSlab">
                          <Form.Label>Concrete/Pad</Form.Label>
                          <Form.Select
                            value={form.hasExistingSlab}
                            onChange={(e) =>
                              update(
                                "hasExistingSlab",
                                e.target.value as HotTubQuizForm["hasExistingSlab"]
                              )
                            }
                          >
                            <option value="">Select</option>
                            <option>Yes</option>
                            <option>No</option>
                            <option>Not sure</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="hasElectrical">
                          <Form.Label>Electrical Ready</Form.Label>
                          <Form.Select
                            value={form.hasElectrical}
                            onChange={(e) =>
                              update(
                                "hasElectrical",
                                e.target.value as HotTubQuizForm["hasElectrical"]
                              )
                            }
                          >
                            <option value="">Select</option>
                            <option>Yes</option>
                            <option>No</option>
                            <option>Not sure</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Notes */}
                    <Form.Group className="mt-2" controlId="notes">
                      <Form.Label>Notes (optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Space details, brand interest, access constraints, etc."
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                      />
                    </Form.Group>

                    <hr className="my-3" />

                    {/* Contact prefs */}
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group controlId="contactPreference">
                          <Form.Label>Contact Preference *</Form.Label>
                          <Form.Select
                            required
                            value={form.contactPreference}
                            onChange={(e) =>
                              update(
                                "contactPreference",
                                e.target.value as HotTubQuizForm["contactPreference"]
                              )
                            }
                            isInvalid={validated && !required(form.contactPreference)}
                          >
                            <option value="">Select preference</option>
                            <option>Call</option>
                            <option>Text</option>
                            <option>Email</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Choose how we should reach you.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="bestTimeToReach">
                          <Form.Label>Best Time to Reach</Form.Label>
                          <Form.Select
                            value={form.bestTimeToReach}
                            onChange={(e) =>
                              update(
                                "bestTimeToReach",
                                e.target.value as HotTubQuizForm["bestTimeToReach"]
                              )
                            }
                          >
                            <option value="">No preference</option>
                            <option>Morning</option>
                            <option>Afternoon</option>
                            <option>Evening</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Consent */}
                    <Form.Check
                      id="consent"
                      className="mt-3"
                      type="checkbox"
                      label="I agree to be contacted about hot tub options and pricing."
                      checked={form.consent}
                      onChange={(e) => update("consent", e.target.checked)}
                      isInvalid={validated && !form.consent}
                      required
                    />
                    {validated && !form.consent && (
                      <div className="invalid-feedback d-block">
                        Please consent so we can contact you.
                      </div>
                    )}

                    {/* Server error */}
                    {serverError && (
                      <Alert variant="danger" className="mt-3">
                        {serverError}
                      </Alert>
                    )}

                    {/* Submit */}
                    <div className="d-grid mt-3">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={submitting || !canSubmit}
                        variant="primary"
                      >
                        {submitting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" /> Submitting…
                          </>
                        ) : (
                          "Get My Recommendations"
                        )}
                      </Button>
                    </div>

                    {/* Trust badges */}
                    <div className="mt-3 d-flex gap-2 flex-wrap">
                      <Badge bg="secondary" pill>
                        No spam
                      </Badge>
                      <Badge bg="secondary" pill>
                        Local experts
                      </Badge>
                      <Badge bg="secondary" pill>
                        Fast quotes
                      </Badge>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          // Confirmation Summary
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="shadow">
                <Card.Header className="bg-success text-white">
                  <strong>Thanks! Your HotTub Quiz Is In.</strong>
                </Card.Header>
                <Card.Body>
                  <Alert variant="success" className="mb-3">
                    We’ll review your answers and reach out with recommendations and pricing.
                    Reference ID: <strong>{confirmation.id}</strong>
                  </Alert>
                  <h6 className="mb-2">Summary</h6>
                  <Row className="g-3 small">
                    {Object.entries(confirmation.payload).map(([k, v]) => (
                      <Col md={6} key={k}>
                        <div className="border rounded p-2 bg-body-tertiary">
                          <div className="text-muted">{k}</div>
                          <div className="fw-semibold">{v || "—"}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <div className="d-flex gap-2 mt-4">
                    <Button variant="secondary" onClick={() => setConfirmation(null)}>
                      Back
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    >
                      Back to Top
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default HotTubQuiz_Landing_v01;
